import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { DcsGhostSettlementService } from './dcs-ghost-settlement.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-dcs-ghost-settlement',
    templateUrl: './dcs-ghost-settlement.component.html',
    styleUrls: ['./dcs-ghost-settlement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DcsGhostSettlementComponent implements OnInit, OnDestroy {

  private static readonly bridgeHostSelector = 'gtw-dcs-ghost-settlement-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly destroy$ = new Subject<void>();
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private isInitialized = false;
  private saveRequestSeq = 0;

  filterParams: any = {};

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  rowData: any = {};

  @Input('modal-name')
  set _modal_name(modalName: string) {
    try {
      if (modalName && modalName.length > 0) {
        this.modalName = modalName;
        this.updateModalConfig();
      }
    } catch (_e) {
      console.log('Modal name input not yet stable. Still waiting to get resolved.');
    }
  }

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to process Transactions?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  showModal: boolean = false;
  isSaveClicked: boolean = false;
  logged_in_user: string = '';
  modalName: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private dcsGhostSettlementService: DcsGhostSettlementService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    if (this.gtwApp?.USER_SETTINGS?.user?.sso_id) {
      this.USER_SETTINGS = this.gtwApp.USER_SETTINGS;
      this.logged_in_user = String(this.gtwApp.USER_SETTINGS.user.sso_id);
    }
    if (this.gtwApp?.SERVER_CONFIG) {
      this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG;
    }
    if ((hydrated as any)?.modalScreen?.modal_name) {
      this.modalName = (hydrated as any).modalScreen.modal_name;
    }
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    this.init();
  }

  init(): void {
    if (
      this.baseURLs &&
      Object.keys(this.baseURLs).length > 0 &&
      this.filterParams &&
      Object.keys(this.filterParams).length > 0 &&
      this.GENERAL_CONFIG &&
      Object.keys(this.GENERAL_CONFIG).length > 0
    ) {
      this.initializeData();
    }
  }

  initializeData(): void {
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    this.updateModalConfig();
    this.cdr.markForCheck();
  }

  updateModalConfig(): void {
    if (this.modalName === 'clearTransactions') {
      this.modalConfig = {
        ...this.modalConfig,
        message: 'Are you sure you want to clear Transactions?'
      };
    } else if (this.modalName === 'processTransactions') {
      this.modalConfig = {
        ...this.modalConfig,
        message: 'Are you sure you want to process Transactions?'
      };
    }
  }

  onConfirm(): void {
    this.confirmSave();
  }

  cancel(): void {
    this.showModal = false;

    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });

    const host = document.querySelector(DcsGhostSettlementComponent.bridgeHostSelector);
    host?.dispatchEvent(updateEvent);
    this.cdr.markForCheck();
  }

  onCancel(): void {
    this.cancel();
  }

  confirmSave(): void {
    this.isSaveClicked = true;

    const ecSaveDtls: any[] = [];
    const returnClobSettingsObj: any = {};

    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['save_type'] = this.modalName;

    const message = 'successfully processed';

    this.sendDetails(returnClobSettingsObj, ecSaveDtls, message);
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.markForCheck();

    const seq = ++this.saveRequestSeq;
    const apiBase =
      this.GENERAL_CONFIG.base_url ||
      this.GENERAL_CONFIG.api ||
      this.baseURLs.api ||
      '';

    this.dcsGhostSettlementService
      .saveGhostSettlement(
        apiBase,
        returnClobSettingsObj,
        ecSaveDtls,
        this.globalService,
        this.filterParams,
        this.rowData
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          if (seq !== this.saveRequestSeq) {
            return;
          }
          this.modalConfig = { ...this.modalConfig, loading: false };

          const response = result.data || result;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this.isSaveClicked = false;
            this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');

            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          if (seq !== this.saveRequestSeq) {
            return;
          }
          this.modalConfig = { ...this.modalConfig, loading: false };
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar('Error occurred while processing. Please try again.', 'gtw-snackbar--red');
          console.error('DCS Ghost Settlement error:', error);
          this.cdr.markForCheck();
        }
      );
  }
}
